import {useEffect, useState, useCallback} from 'react';
import {useCheckInPatientHook} from '../../hooks/useCheckInPatientHook';
import {PatchCheckInPatientPayloadType} from '$api/frontdesk';
import CheckInTimerComponent from './component';
import useAuthUser from '../../../../../hooks/useAuthUser';
import {UserRoleName} from '$modules/admin/constant';
import useAppointmentTimerSync, {
  TimerSyncData,
} from '../../../../../hooks/useAppointmentTimerSync';

interface CheckInTimerProps {
  startTime: string;
  endTime?: string;
  appointmentId: number;
  onConsultantEndConsultation?: () => void;
  disabled?: boolean;
  onStopTimer?: () => void;
}

const CheckInTimer = (props: CheckInTimerProps) => {
  const {
    appointmentId,
    startTime,
    endTime: propEndTime,
    onConsultantEndConsultation,
    onStopTimer,
  } = props;
  const checkInTime = new Date(startTime).getTime();
  const [endTime, setEndTime] = useState<string | undefined>(propEndTime);
  const checkOutTime = endTime ? new Date(endTime).getTime() : null;
  const {sendCheckInPatient} = useCheckInPatientHook();
  const [currentTime, setCurrentTime] = useState<number>(new Date().getTime());
  const user = useAuthUser();
  const isDoctor = user?.roleName === UserRoleName.Doctor;

  useAppointmentTimerSync(appointmentId, (data: TimerSyncData) => {
    if (data.action === 'stop_timer') {
      setEndTime(new Date().toISOString());
    }
  });

  useEffect(() => {
    setEndTime(propEndTime);
  }, [propEndTime]);

  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | null = null;
    if (!endTime) {
      intervalId = setInterval(() => {
        setCurrentTime(new Date().getTime());
      }, 1000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [endTime, appointmentId]);

  const elapsedTime = checkOutTime
    ? checkOutTime - checkInTime
    : currentTime - checkInTime;
  const currentMinutes = Math.floor(
    (elapsedTime % (1000 * 60 * 60)) / (1000 * 60)
  );
  const currentSeconds = Math.floor((elapsedTime % (1000 * 60)) / 1000);

  const handleEndTimer = useCallback(() => {
    if (isDoctor && onConsultantEndConsultation) {
      onConsultantEndConsultation();
      return;
    }
    if (!isDoctor && onStopTimer) {
      onStopTimer();
      return;
    }
    const payload: PatchCheckInPatientPayloadType = {
      appointmentId,
      check_out_time: new Date().toISOString(),
      check_in_time: startTime,
    };
    sendCheckInPatient(payload);
    setEndTime(new Date().toISOString());
  }, [
    isDoctor,
    onConsultantEndConsultation,
    appointmentId,
    sendCheckInPatient,
    startTime,
    onStopTimer,
  ]);

  return (
    <CheckInTimerComponent
      currentMinutes={currentMinutes}
      currentSeconds={currentSeconds}
      handleEndTimer={handleEndTimer}
      showStopTimer={!endTime}
      isDoctor={isDoctor}
      disabled={props.disabled}
    />
  );
};

export default CheckInTimer;
